import {
    makeExecutorOwnerDetailsUrl,
    makeSellerUrl,
    makeServiceRequesterUrl,
    trans,
} from '@frontend/helpers'
import type {CategoryType} from '@shared/types/Categories'
import clsx from 'clsx'
import {
    type ReactNode,
    useState,
} from 'react'
import {Link} from 'react-router-dom'
import {Button} from 'swayok-react-mdb-ui-kit/components/Button/Button'
import {RatingStars} from 'swayok-react-mdb-ui-kit/components/Icon/RatingStars'
import {UserAvatar} from '../../../../components/UserAvatar'
import type {ServiceRequesterDetailsData} from '../../service_requester_info/ServiceRequesterInfoModuleTypes'
import type {SellerDetailsData} from '../SellerInfoModuleTypes'
import {SellerReviewsListModal} from './SellerReviewsListModal'

interface Props {
    seller: SellerDetailsData | ServiceRequesterDetailsData
    noAvatar?: boolean
    noRegistrationDate?: boolean
    categoryType: CategoryType
    noLinkToProfile?: boolean
    noReviewsModal?: boolean
    hideEmptyReviews?: boolean
    className?: string
    ratingBlockClassName?: string
    registrationDateClassName?: string
    // Раскладка блока: 'default' - имя в строке с аватаром, рейтинг отдельной
    // строкой под ними; 'stacked' - имя, звезды и отзывы стоят колонкой справа
    // от аватара, числовое значение рейтинга не показывается (макет 577:8995).
    layout?: 'default' | 'stacked'
    Menu?: ReactNode | ReactNode[]
}

// Информация о продавце, исполнителе или заказчике услуг.
export function SellerInfo(props: Props) {

    const {
        seller,
        noAvatar,
        noRegistrationDate,
        categoryType,
        noLinkToProfile,
        noReviewsModal,
        hideEmptyReviews,
        className,
        ratingBlockClassName = 'mt-3',
        registrationDateClassName = 'mt-2 text-center',
        layout = 'default',
        Menu,
    } = props

    const isStacked: boolean = layout === 'stacked'

    // Видимость окна со списком отзывов.
    const [
        isReviewsModalVisible,
        setIsReviewsModalVisible,
    ] = useState<boolean | null>(null)

    const getSellerUrl = (): string => {
        switch (categoryType) {
            case 'service_requests':
                return makeServiceRequesterUrl(seller.id)
            case 'services':
                return makeExecutorOwnerDetailsUrl(seller.id)
            default:
                return makeSellerUrl(seller.id)
        }
    }

    const nameNode: ReactNode = noLinkToProfile ? (
        <span className="fw-500">
            {seller.name}
        </span>
    ) : (
        <Link
            to={getSellerUrl()}
            className="fw-500"
            target="_blank"
            rel="noreferrer"
        >
            {seller.name}
        </Link>
    )

    const reviewsNode: ReactNode = seller.reviewsCount > 0 && !noReviewsModal ? (
        <Button
            color="link"
            className="link-blue text-nowrap side-nav-link"
            onClick={() => setIsReviewsModalVisible(true)}
        >
            {trans().reviews.reviews_count(seller.reviewsCount)}
        </Button>
    ) : (
        !(hideEmptyReviews && seller.reviewsCount === 0) && (
            <div className="text-nowrap">
                {trans().reviews.reviews_count(seller.reviewsCount)}
            </div>
        )
    )

    const ratingNode: ReactNode = (
        <div
            className={clsx(
                isStacked
                    ? 'flex-container-column align-items-start'
                    : 'flex-row-between flex-wrap',
                ratingBlockClassName
            )}
        >
            {isStacked && seller.rating > 0 && (
                <RatingStars
                    rating={seller.rating}
                    starSize={20}
                />
            )}
            {!isStacked && seller.rating > 0 && (
                <div className="flex-row-center-vertical gap-2">
                    <div className="fw-500">
                        {seller.rating}
                    </div>
                    <RatingStars
                        rating={seller.rating}
                        starSize={20}
                    />
                </div>
            )}
            {reviewsNode}
        </div>
    )

    return (
        <div className={className}>
            <div className="flex-container-row align-items-center justify-content-between">
                {/* Утилита flex-1 растягивает строку, чтобы отжать меню вправо.
                    В колоночной раскладке меню нет, а растянутая строка ломает
                    место под дату регистрации, поэтому там утилита не нужна. */}
                <div
                    className={clsx(
                        'flex-container-row align-items-center gap-3',
                        !isStacked && 'flex-1'
                    )}
                >
                    {!noAvatar && (
                        <UserAvatar
                            userName={seller.name}
                            // В колоночной раскладке зазор до текста задает
                            // родитель через gap: в макете он шире утилиты.
                            className={clsx(!isStacked && 'me-3')}
                        />
                    )}
                    {isStacked ? (
                        <div className="flex-container-column">
                            {nameNode}
                            {ratingNode}
                        </div>
                    ) : nameNode}
                </div>
                {Menu}
            </div>
            {!isStacked ? ratingNode : null}
            {!noRegistrationDate && (
                <div
                    className={clsx(
                        'text-muted fs-7',
                        registrationDateClassName
                    )}
                >
                    {trans().seller.on_service_since(seller.registrationDate)}
                </div>
            )}
            {!noReviewsModal && (
                <SellerReviewsListModal
                    seller={seller}
                    show={isReviewsModalVisible}
                    onClose={() => {
                        setIsReviewsModalVisible(false)
                    }}
                />
            )}
        </div>
    )
}
